<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3>Preparation Steps</h3>
                <a href="#" @click.prevent="openForm">
                    <span>New Step</span> <i class="fas fa-plus-circle"></i>
                </a>
            </b-col>
        </b-row>
        <b-row v-if="showForm">
            <b-col cols="12">
                <b-card>
                    <b-row>
                        <b-col cols="2">
                            <fg-input v-model.number="upsertStepNumber" label="Step #" type="number" required></fg-input>
                        </b-col>
                        <b-col cols="10">
                            <fg-input v-model="upsertDescription" label="Description" type="text" required></fg-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequireWeight">Require Weight</b-form-checkbox>
                        </b-col>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequirePicture">Require Picture</b-form-checkbox>
                        </b-col>
                        <b-col cols="3">
                            <b-form-checkbox v-model="upsertRequirePharmacistSignOff">Require Pharmacist Sign-Off</b-form-checkbox>
                        </b-col>
                        <b-col cols="3" v-if="upsertRequireWeight">
                            <fg-input v-model.number="upsertExpectedWeight" label="Expected Weight" type="number" step="0.01" required></fg-input>
                        </b-col>
                        <b-col cols="3" v-if="upsertRequireWeight">
                            <fg-input v-model.number="upsertWeightTolerance" label="Tolerance (%)" type="number" step="0.1" required></fg-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12">
                            <b-button variant="success" @click="submitUpsert" :disabled="!canSaveStep">Save</b-button>
                            <b-button @click="onCancelClick">Cancel</b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table :fields="stepFields" :items="sortedSteps">
                    <template #cell(requireWeight)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(requirePicture)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(requirePharmacistSignOff)="data">
                        <b-icon v-if="data.value" icon="check-circle-fill" variant="success"></b-icon>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right">
                            <a href="#" @click.prevent="updateStep(data.item)">
                                <b-icon v-b-tooltip="'Edit'" icon="pencil-square"/>
                            </a>
                            <a href="#" @click.prevent="deleteConfirmDialog(data.item)">
                                <b-icon v-b-tooltip="'Remove'" class="mx-1" icon="x-circle-fill" variant="danger"/>
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
    import { CompoundSteps } from '@/models/Drug/CompoundDrug';

    @Component({
        name: "PreparationStepsComponent",
        components: {}
    })
    export default class PreparationStepsComponent extends Vue {

        @Prop() private compoundId!: number;
        @Prop() private steps!: CompoundSteps[];

        private showForm: boolean = false;
        private upsertId: number = 0;
        private upsertStepNumber: number = 1;
        private upsertDescription: string = "";
        private upsertRequireWeight: boolean = false;
        private upsertRequirePicture: boolean = false;
        private upsertWeightTolerance: number | null = null;
        private upsertExpectedWeight: number | null = null;
        private upsertRequirePharmacistSignOff: boolean = false;

        private stepFields = [
            { key: "stepNumber", label: "Step #" },
            { key: "description", label: "Description" },
            { key: "requireWeight", label: "Req. Weight" },
            { key: "requirePicture", label: "Req. Picture" },
            { key: "expectedWeight", label: "Expected Weight" },
            { key: "weightTolerance", label: "Tolerance (%)" },
            { key: "requirePharmacistSignOff", label: "RPh Sign-Off" },
            { key: "actions", label: "" },
        ]

        get sortedSteps() {
            return this.steps ? [...this.steps].sort((a, b) => a.stepNumber - b.stepNumber) : [];
        }

        get canSaveStep(): boolean {
            // Step number and description are always required
            if (!this.upsertStepNumber || !this.upsertDescription) {
                return false;
            }

            // If weight is required, expected weight and tolerance must be populated
            if (this.upsertRequireWeight) {
                if (this.upsertExpectedWeight == null || this.upsertWeightTolerance == null) {
                    return false;
                }
            }

            return true;
        }

        submitUpsert() {
            // Validate before submitting
            if (!this.canSaveStep) {
                this.$notification.error('Please fill in all required fields. When weight is required, expected weight and tolerance must be provided.');
                return;
            }
            const step = new CompoundSteps();
            step.id = this.upsertId || 0;
            step.compoundID = this.compoundId;
            step.stepNumber = this.upsertStepNumber;
            step.description = this.upsertDescription;
            step.requireWeight = this.upsertRequireWeight;
            step.requirePicture = this.upsertRequirePicture;
            step.weightTolerance = this.upsertWeightTolerance;
            step.expectedWeight = this.upsertExpectedWeight;
            step.requirePharmacistSignOff = this.upsertRequirePharmacistSignOff;

            const existingIndex = this.steps.findIndex(item => item.id === step.id);
            if (existingIndex > -1) {
                this.steps.splice(existingIndex, 1);
            }
            this.steps.push(step);
            this.closeForm();
            this.$emit('continue');
        }

        updateStep(item: CompoundSteps) {
            this.showForm = true;
            this.upsertId = item.id;
            this.upsertStepNumber = item.stepNumber;
            this.upsertDescription = item.description;
            this.upsertRequireWeight = item.requireWeight;
            this.upsertRequirePicture = item.requirePicture;
            this.upsertWeightTolerance = item.weightTolerance;
            this.upsertExpectedWeight = item.expectedWeight;
            this.upsertRequirePharmacistSignOff = item.requirePharmacistSignOff || false;
        }

        deleteConfirmDialog(item: CompoundSteps) {
            this.$bvModal.msgBoxConfirm(`This preparation step will be deleted. Are you sure?`, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(value => {
                    if (!value) return;
                    this.deleteStep(item);
                })
                .catch(err => {
                    console.error("Error caught on Delete button.", err);
                });
        }

        deleteStep(item: CompoundSteps) {
            if (!item) return;
            const itemIndex = this.steps.findIndex(s => s.id === item.id);
            if (itemIndex < 0) return;
            this.steps.splice(itemIndex, 1);
            this.$emit('delete');
        }

        openForm() {
            this.clearForm();
            this.showForm = true;
        }

        closeForm() {
            this.clearForm();
            this.showForm = false;
        }

        clearForm() {
            this.upsertId = Math.min(...this.steps.map(item => item.id), 0) - 1;
            if (this.upsertId > 0) this.upsertId = 0;
            this.upsertStepNumber = this.steps.length > 0 ? Math.max(...this.steps.map(s => s.stepNumber)) + 1 : 1;
            this.upsertDescription = "";
            this.upsertRequireWeight = false;
            this.upsertRequirePicture = false;
            this.upsertWeightTolerance = null;
            this.upsertExpectedWeight = null;
            this.upsertRequirePharmacistSignOff = false;
        }

        @Emit('cancel')
        onCancelClick() {
            this.closeForm();
        }
    }
</script>
